import { memo, useState, useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import Avatar from "../../misc/Avatar"
import { getNameShortForm } from "../SoftphoneUtil"
import * as callIntelligence from "../../actions/addon_call_intelligence"
import { formatNumber, generate } from "../../tools"
import { fromJS } from "immutable"
import Theme from "./Theme"

import Icon from "../../Icon"

const uuidv4 = () => generate(36)

export const EnvInit = dispatch => {
    callIntelligence
        .env()
        .then(data => dispatch(callIntelligence.setEnv(data)))
        .catch(err => console.log(err))
}

const CallIntelligencePanelEnabler = memo(props => {
    const {
        addonCallIntelligence: enabled,
        addonCallIntelligenceOptions: options,
        agent,
    } = useSelector(state => state.options)
    const { subscribed } = useSelector(state => state.callIntelligence)
    const { propertyInfo } = useSelector(state => state.dialParams)

    if (!enabled || !subscribed) return <></>
    return <CallIntelligencePanel {...props} options={options} agent={agent} propertyInfo={propertyInfo} />
})

const CallIntelligencePanel = Theme(
    memo(props => {
        const dispatch = useDispatch()
        const { messages, diff, userInitials, trial } = useSelector(state => state.callIntelligence)
        const [avail, setAvail] = useState(true)
        const [finishExecuted, setFinishExecuted] = useState(false)
        const [aiGreepings, setAIGreepings] = useState(false)
        const [isExpanded, setIsExpanded] = useState(
            JSON.parse(sessionStorage.getItem("show_call_intelligence") || "true")
        )
        const [onCall, setOnCall] = useState(0)
        const [aiInProgress, setAIInProgress] = useState(false)
        const setMessage = msg => dispatch(callIntelligence.setMessage(msg))
        const [property, setProperty] = useState(props.propertyInfo)

        const reInit = callid => {
            dispatch(callIntelligence.clear())
            setOnCall(callid)
            setFinishExecuted(false)
            setProperty(() => props.propertyInfo)
        }

        useEffect(() => {
            const isExpandedGlobal = JSON.parse(sessionStorage.getItem("show_call_intelligence") || "false")
            if (props.minimized && isExpandedGlobal) {
                setIsExpanded(false)
            } else if (!props.minimized && isExpandedGlobal) {
                setIsExpanded(true)
            }
        }, [props.minimized])

        useEffect(() => {
            if (isExpanded) {
                callIntelligence
                    .available()
                    .then(data => setAvail(!!data?.available))
                    .catch(e => setAvail(null))
            }
        }, [avail, isExpanded])

        useEffect(() => {
            if (!avail) return
            if (props?.call?.id && !onCall) {
                reInit(props?.call?.id)
            } else if (props?.call?.id && onCall && !aiGreepings) {
                const options = {
                    ...(props?.agent ? { override: { agent: props.agent } } : {}),
                    debug: props?.options?.debug,
                }
                callIntelligence
                    .init(props.call.id, options)
                    .catch(e => console.log(e))
                    .finally(() => {
                        getNextPhrase(true)
                        setAIGreepings(true)
                    })
            } else if (avail && !props?.call?.id && onCall && !finishExecuted) {
                finishCall()
                setOnCall(() => 0)
                setFinishExecuted(() => true)
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [props.call, onCall, avail, finishExecuted])

        useEffect(() => {
            if (!avail || !props?.options?.propertyLoad) return
            if (props?.call?.contactid && !property) {
                Promise.all([
                    dispatch(callIntelligence.getProperty(props?.call?.contactid)),
                    dispatch(callIntelligence.getPropertyImages(props?.call?.contactid)),
                ])
                    .then(([property, images]) => {
                        const prop = {}
                        prop.bathrooms = property?.get("building")?.get("bathroomCount")
                        prop.bedrooms = property.get("building").get("bedroomCount")
                        prop.size = property.get("building").get("totalBuildingAreaSquareFeet")
                        prop.lotSize = property.get("lot").get("lotSizeSquareFeet")
                        prop.img = images.get(0)
                        prop.address = `${property?.get("address")?.get("street")}, ${property
                            ?.get("address")
                            ?.get("city")} ${property?.get("address")?.get("state")}, ${property
                            ?.get("address")
                            ?.get("zip")}`

                        setProperty(prop)
                    })
                    .catch(err => console.log(err))
            }
        }, [dispatch, props.call, property])

        useEffect(() => {
            window.addEventListener("call_intelligence_call_transcription", e => setMessage(e.detail))
            return () => window.removeEventListener("call_intelligence_call_transcription", e => setMessage(e.detail))
        }, [])

        const getNextPhrase = (greeting = false) => {
            if (!avail || !onCall) {
                return
            }

            const currentDiff = [...diff.filter(t => !t?.ignore)]
            dispatch(callIntelligence.clearDiff())
            setAIInProgress(true)
            callIntelligence
                .next_phrase(onCall, currentDiff)
                .then(data =>
                    data.forEach(text => {
                        if (greeting === true) {
                            setMessage({ role: "ai", ts: 0, id: uuidv4(), text })
                        } else {
                            setMessage({ role: "ai", ts: new Date().getTime(), id: uuidv4(), text })
                            window.dispatchEvent(new CustomEvent("transcriber_split_transcription"))
                        }
                    })
                )
                .catch(e => console.log(e))
                .finally(() => setAIInProgress(false))
        }

        const finishCall = () => {
            if (!avail) return
            const currentDiff = [...diff.filter(t => !t?.ignore)]
            callIntelligence
                .finish(onCall, currentDiff)
                .then(data => {
                    if (!props.setCallIntelligenceFinish) {
                        return
                    }
                    props.setCallIntelligenceFinish(data)
                })
                .catch(e => console.log(e))
        }

        const toggleCallIntelligencePanel = () => {
            if (props?.minimized) return
            sessionStorage.setItem("show_call_intelligence", JSON.stringify(!isExpanded))
            setIsExpanded(!isExpanded)
        }

        return (
            <>
                {!isExpanded ? (
                    props?.minimized ? (
                        <span className={`softphone-ai-icon minimized`} onClick={toggleCallIntelligencePanel}>
                            <span>
                                <img src={props.images.CallIntelligenceIcon} alt="" />
                            </span>
                        </span>
                    ) : (
                        <span
                            className={`softphone-ai-icon ${isExpanded ? "expanded" : ""}`}
                            onClick={toggleCallIntelligencePanel}>
                            <span>
                                <img src={props.images.CallIntelligenceIcon} alt="" />
                            </span>
                            <span className="hider cursor_pointer call-intelligence-svg d-flex align-items-center justify-content-center w-100">
                                <img src={props.images.IconChevronLeft} alt="" />
                            </span>
                        </span>
                    )
                ) : null}

                {isExpanded && !props?.minimized && (
                    <div className="call-intelligence-panel">
                        {avail ? (
                            <CallIntelligence
                                toggleCallIntelligencePanel={toggleCallIntelligencePanel}
                                messages={messages}
                                isTrial={trial || props?.options?.trialBadge}
                                onCall={onCall}
                                getNextPhrase={getNextPhrase}
                                aiInProgress={aiInProgress}
                                userInitials={userInitials}
                                property={property}
                                {...props}
                            />
                        ) : (
                            <UnAvailable
                                toggleCallIntelligencePanel={toggleCallIntelligencePanel}
                                handleShowIntercomChat={props.eventHandler?.onClickHelpWanted}
                                images={props.images}
                            />
                        )}
                    </div>
                )}
            </>
        )
    })
)

const CallIntelligence = memo(
    ({
        toggleCallIntelligencePanel,
        contact,
        messages,
        isTrial,
        onCall,
        getNextPhrase,
        aiInProgress,
        userInitials,
        property,
        agent,
        images,
        theme,
    }) => {
        const scrollRef = useRef()

        useEffect(() => {
            if (scrollRef.current) {
                setTimeout(() => {
                    if (scrollRef?.current) {
                        scrollRef.current.scrollTop = scrollRef.current.scrollHeight
                    }
                }, 50)
            }
        }, [messages])

        return (
            <div className="h-100 d-flex flex-column call-intelligence">
                <span
                    onClick={toggleCallIntelligencePanel}
                    onMouseDown={e => e.stopPropagation()}
                    className="softphone-ai-icon position-absolute cursor_pointer call-intelligence-svg d-flex align-items-center justify-content-center">
                    <img className="hider" src={images.IconChevronRight} alt="" />
                </span>

                {property && (
                    <div className="d-flex gap-3 align-items-center call-intelligence-border-bottom px-3 py-3 property-header">
                        {!!property?.img && (
                            <img
                                src={property.img}
                                id="bd-ci-p-img"
                                alt=""
                                style={{ maxWidth: "85px", maxHeight: "60px", borderRadius: "5px" }}
                            />
                        )}
                        <div>
                            <span className="dialer_text">CALLING ABOUT</span>
                            {!!property?.address && (
                                <div className={`dialer_text ${!property?.img ? "no_image" : ""}`}>
                                    <span>{property.address}</span>
                                </div>
                            )}
                            <div className="d-flex align-items-center dialer_text call-intelligence-property-splitter">
                                {!!property?.bedrooms && (
                                    <span className="d-flex gap-2 align-items-center call-intelligence-property">
                                        <img src={images.IconBed} alt="" /> <span>{property?.bedrooms}</span>
                                    </span>
                                )}
                                {!!property?.bathrooms && (
                                    <span className="d-flex gap-2 align-items-center call-intelligence-property">
                                        <img src={images.IconBath} alt="" /> <span>{property?.bathrooms}</span>
                                    </span>
                                )}
                                {!!property?.size && (
                                    <span className=" d-flex gap-2 align-items-center call-intelligence-property">
                                        <img src={images.IconSQFT} alt="" /> <span>{formatNumber(property?.size)}</span>{" "}
                                        SQFT
                                    </span>
                                )}
                                {!!property?.lotSize && (
                                    <span className=" d-flex gap-2 align-items-center call-intelligence-property">
                                        <img src={images.IconLotSize} alt="" />{" "}
                                        <span>{formatNumber(property?.lotSize)}</span> LOT
                                    </span>
                                )}
                            </div>
                        </div>
                        {isTrial && <TrialBadge absolute images={images} />}
                    </div>
                )}

                {/* Chat Section */}
                <div className="dialer-chat-section px-4 py-2 my-2 mx-1" ref={scrollRef}>
                    <div className="dialer-chat-messages">
                        <div className="d-flex justify-content-center gap-2 align-items-center flex-row font-12 pb-2 copyright">
                            {!property && isTrial && <TrialBadge images={images} />}
                            <img src={images.CallIntelligenceAIAvatar} alt="" width="18" height="18" />
                            Powered by {`${theme === "propstream" ? "PropStream Intelligence" : "Batch Dialer"}`}
                        </div>
                        {messages.map(
                            message =>
                                !message.ignore && (
                                    <div
                                        className={`d-flex gap-2 ${
                                            message.role === "agent" ? "flex-row-reverse" : "flex-row"
                                        }`}
                                        key={message?.ts}>
                                        {message?.role === "agent" ? (
                                            <Avatar
                                                {...{
                                                    user: agent ? fromJS(agent) : undefined,
                                                    forcetext: userInitials || "ME",
                                                }}
                                                className={message.role}
                                            />
                                        ) : message?.role === "ai" ? (
                                            <Avatar
                                                forceimg={images.CallIntelligenceAIAvatar}
                                                className={message.role}
                                            />
                                        ) : (
                                            <Avatar forcetext={getNameShortForm(contact)} className={message.role} />
                                        )}
                                        <div className={`dialer-chat-message ${message?.role}`}>
                                            {message?.role === "ai" ? (
                                                <span className="dialer-sender">Suggested Message:</span>
                                            ) : null}
                                            <div className="d-flex gap-2 align-items-center">
                                                <p className="dialer-message-text">{message.text}</p>
                                            </div>
                                        </div>
                                    </div>
                                )
                        )}
                    </div>
                </div>

                <div className="mt-auto d-flex justify-content-center align-items-center p-3 call-intelligence-border-top">
                    <button
                        className="call-intelligence-btn"
                        onClick={getNextPhrase}
                        onMouseDown={e => e.stopPropagation()}
                        disabled={aiInProgress || !onCall}>
                        {!!aiInProgress ? <Icon icon="spinner" spin /> : <img src={images.LogoIcon} alt="" />} What
                        should I say?
                    </button>
                </div>
            </div>
        )
    }
)

const TrialBadge = ({ absolute, images }) => {
    return (
        <div
            className={`dialer-trial-badge d-flex gap-2 justify-content-center ${
                absolute && "position-absolute"
            } align-items-center text-white fw-500`}>
            <img src={images.StarIcon} alt="" />
            <span>Trial Only</span>
        </div>
    )
}

const UnAvailable = ({ toggleCallIntelligencePanel, handleShowIntercomChat, images }) => {
    return (
        <div className="h-100 d-flex flex-column align-items-center justify-content-center">
            <span
                onClick={toggleCallIntelligencePanel}
                className="softphone-ai-icon position-absolute cursor_pointer call-intelligence-svg d-flex align-items-center justify-content-center">
                <img className="hider" src={DialerCloseAIIcon} alt="" />
            </span>
            <div className="call-intelligence-content px-4 py-3">
                <img src={images.CallIntelligenceUnavailable} alt="" />
            </div>
            <div className="call-intelligence-content px-4 py-3 text-center">
                <div className="panel-content">
                    <h1 className="modal-heading my-4" style={{ fontSize: "25px", fontWeight: 500, color: "#000" }}>
                        Call Intelligence currently unavailable
                    </h1>
                </div>
                <div className="panel-content">Please try again later</div>
            </div>

            {!!handleShowIntercomChat && (
                <div className="call-intelligence-chat-section px-4 py-3" style={{ position: "absolute", bottom: 0 }}>
                    Have questions?
                    <span className="link" onClick={handleShowIntercomChat}>
                        Chat with us
                    </span>
                </div>
            )}
        </div>
    )
}

export default CallIntelligencePanelEnabler
